import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  Form,
  FormField,
  Input,
  Select,
  RadioGroup,
  SpaceBetween,
  Button,
  Container,
  Header,
  ColumnLayout,
  Alert,
  Textarea,
} from '@cloudscape-design/components';
import type { SelectProps } from '@cloudscape-design/components';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { useRules } from '../../hooks/useRules';
import { RESOURCE_TYPES } from '../../constants/resourceTypes';
import type { EvaluationSubject } from '@amzn/helianthus-arbiter-client';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export function CreateEvaluationPage() {
  useDocumentTitle('Create Evaluation');
  const navigate = useNavigate();
  const client = useArbiterClient();

  // Subject type toggle
  const [subjectType, setSubjectType] = useState<string>('awsResource');

  // AWS Resource fields
  const [resourceType, setResourceType] = useState<SelectProps.Option | null>(null);
  const [resourceId, setResourceId] = useState('');

  // Code Package fields
  const [packageName, setPackageName] = useState('');
  const [commitId, setCommitId] = useState('');
  const [versionSet, setVersionSet] = useState('');
  const [branchName, setBranchName] = useState('');

  // Code Diff fields
  const [rawDiff, setRawDiff] = useState('');

  // Configuration
  const [bindleLock, setBindleLock] = useState('');
  const [snsTopic, setSnsTopic] = useState('');

  // Form state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter out code::package from resource types when AWS Resource is selected (FORM-01)
  const filteredResourceTypes = useMemo(
    () => RESOURCE_TYPES.filter((opt) => opt.value !== 'code::package'),
    [],
  );

  // Matching rule count
  const selectedResourceType =
    subjectType === 'awsResource'
      ? resourceType?.value
      : subjectType === 'code'
        ? 'code::package'
        : undefined;
  const { rules: matchingRules } = useRules({
    resourceType: selectedResourceType || undefined,
  });
  const matchingCount = matchingRules.filter(
    (r) => r.ruleStatus === 'active',
  ).length;

  // Reset all subject-specific fields when switching radio options
  const handleSubjectTypeChange = (value: string) => {
    setSubjectType(value);
    setResourceType(null);
    setResourceId('');
    setPackageName('');
    setCommitId('');
    setVersionSet('');
    setBranchName('');
    setRawDiff('');
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate required fields
    if (subjectType === 'awsResource') {
      if (!resourceType?.value) {
        setError('Resource type is required');
        return;
      }
      if (!resourceId.trim()) {
        setError('Resource ID is required');
        return;
      }
    } else if (subjectType === 'code') {
      if (!packageName.trim()) {
        setError('Package name is required');
        return;
      }
      if (!commitId.trim()) {
        setError('Commit ID is required');
        return;
      }
    } else if (subjectType === 'codeDiff') {
      if (!rawDiff.trim()) {
        setError('Diff content is required');
        return;
      }
      if (!packageName.trim()) {
        setError('Package name is required');
        return;
      }
    }
    if (!bindleLock.trim()) {
      setError('Bindle lock is required');
      return;
    }

    setSubmitting(true);
    try {
      let subject: EvaluationSubject;
      if (subjectType === 'awsResource') {
        subject = {
          awsResource: {
            resourceType: resourceType!.value!,
            resourceId: resourceId.trim(),
          },
        } as EvaluationSubject.AwsResourceMember;
      } else if (subjectType === 'codeDiff') {
        // codeDiff is not in the generated EvaluationSubject union.
        // Construct as $UnknownMember -- backend handles this via extension.
        subject = {
          $unknown: ['codeDiff', {
            rawDiff: rawDiff.trim(),
            packageName: packageName.trim(),
            versionSet: versionSet.trim() || undefined,
          }],
        } as EvaluationSubject.$UnknownMember;
      } else {
        subject = {
          code: {
            packageName: packageName.trim(),
            commitId: commitId.trim(),
            versionSet: versionSet.trim() || undefined,
            branchName: branchName.trim() || undefined,
          },
        } as EvaluationSubject.CodeMember;
      }

      const response = await client.createEvaluation({
        subject,
        bindleLock: bindleLock.trim(),
        snsTopic: snsTopic.trim() || undefined,
      });
      navigate(`/evaluations/${response.evaluation?.evaluationId}`);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create evaluation',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => navigate('/evaluations')}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            onClick={handleSubmit}
          >
            Run Evaluation
          </Button>
        </SpaceBetween>
      }
      header={
        <Header
          variant="h1"
          description="Evaluate a resource, code package, or code diff against all applicable security rules"
        >
          Create Evaluation
        </Header>
      }
      errorText={error}
    >
      <SpaceBetween size="l">
        {/* Subject */}
        <Container header={<Header variant="h2">Subject</Header>}>
          <SpaceBetween size="l">
            <RadioGroup
              value={subjectType}
              onChange={({ detail }) => handleSubjectTypeChange(detail.value)}
              items={[
                { value: 'awsResource', label: 'AWS Resource' },
                { value: 'code', label: 'Code Package' },
                { value: 'codeDiff', label: 'Code Diff' },
              ]}
            />

            {subjectType === 'awsResource' && (
              <ColumnLayout columns={2}>
                <FormField label="Resource Type" constraintText="Required">
                  <Select
                    selectedOption={resourceType}
                    onChange={({ detail }) =>
                      setResourceType(detail.selectedOption)
                    }
                    options={filteredResourceTypes}
                    placeholder="Select a resource type"
                    filteringType="auto"
                  />
                </FormField>
                <FormField label="Resource ID" constraintText="Required">
                  <Input
                    value={resourceId}
                    onChange={({ detail }) => setResourceId(detail.value)}
                    placeholder="arn:aws:s3:::my-bucket"
                  />
                </FormField>
              </ColumnLayout>
            )}

            {subjectType === 'code' && (
              <>
                <ColumnLayout columns={2}>
                  <FormField label="Package Name" constraintText="Required">
                    <Input
                      value={packageName}
                      onChange={({ detail }) => setPackageName(detail.value)}
                      placeholder="MyServicePackage"
                    />
                  </FormField>
                  <FormField label="Commit ID" constraintText="Required">
                    <Input
                      value={commitId}
                      onChange={({ detail }) => setCommitId(detail.value)}
                      placeholder="abc123def456"
                    />
                  </FormField>
                </ColumnLayout>
                <ColumnLayout columns={2}>
                  <FormField label="Version Set">
                    <Input
                      value={versionSet}
                      onChange={({ detail }) => setVersionSet(detail.value)}
                      placeholder="Optional"
                    />
                  </FormField>
                  <FormField label="Branch Name">
                    <Input
                      value={branchName}
                      onChange={({ detail }) => setBranchName(detail.value)}
                      placeholder="Optional"
                    />
                  </FormField>
                </ColumnLayout>
              </>
            )}

            {/* TODO: Add CR URL field when backend model adds crUrl to CodeDiffSubject */}
            {subjectType === 'codeDiff' && (
              <SpaceBetween size="l">
                <FormField
                  label="Diff content"
                  constraintText="Required"
                  description="Paste the raw diff text to evaluate against security rules"
                >
                  <Textarea
                    value={rawDiff}
                    onChange={({ detail }) => setRawDiff(detail.value)}
                    placeholder="Paste diff content here..."
                    rows={8}
                  />
                </FormField>
                <ColumnLayout columns={2}>
                  <FormField label="Package name" constraintText="Required" description="Which code package the diff is for">
                    <Input
                      value={packageName}
                      onChange={({ detail }) => setPackageName(detail.value)}
                      placeholder="MyServicePackage"
                    />
                  </FormField>
                  <FormField label="Version set" description="Optional context for rule matching">
                    <Input
                      value={versionSet}
                      onChange={({ detail }) => setVersionSet(detail.value)}
                      placeholder="MyVersionSet/mainline"
                    />
                  </FormField>
                </ColumnLayout>
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>

        {/* Configuration */}
        <Container header={<Header variant="h2">Configuration</Header>}>
          <ColumnLayout columns={2}>
            <FormField label="Bindle Lock" constraintText="Required">
              <Input
                value={bindleLock}
                onChange={({ detail }) => setBindleLock(detail.value)}
                placeholder="bindle-lock-id"
              />
            </FormField>
            <FormField label="SNS Topic">
              <Input
                value={snsTopic}
                onChange={({ detail }) => setSnsTopic(detail.value)}
                placeholder="Optional"
              />
            </FormField>
          </ColumnLayout>
        </Container>

        {/* Matching rules info */}
        {selectedResourceType && (
          <Alert type="info">
            All active rules that apply to{' '}
            <strong>{selectedResourceType}</strong> will be evaluated
            automatically. Currently {matchingCount} rules match this resource
            type.
          </Alert>
        )}
      </SpaceBetween>
    </Form>
  );
}
